import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Alert,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Tab,
  Tabs,
  LinearProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import BackupIcon from '@mui/icons-material/Backup';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import StorageIcon from '@mui/icons-material/Storage';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RestoreIcon from '@mui/icons-material/Restore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AddIcon from '@mui/icons-material/Add';
import { backupService, BackupJob, BackupHistory, BackupHealth } from '../api/services/backup';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const BackupManagement: React.FC = () => {
  const [health, setHealth] = useState<BackupHealth | null>(null);
  const [jobs, setJobs] = useState<BackupJob[]>([]);
  const [history, setHistory] = useState<BackupHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [healthData, jobsData, historyData] = await Promise.all([
        backupService.getHealthCheck(),
        backupService.getJobs({ limit: 100 }),
        backupService.getHistory({ limit: 100 }),
      ]);

      setHealth(healthData);
      setJobs(jobsData.backup_jobs || []);
      setHistory(historyData.history || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load backup data');
      console.error('Error fetching backup data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRunJob = async (jobId: string) => {
    try {
      await backupService.runJob(jobId);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to run backup job');
    }
  };

  const getHealthColor = (health: string) => {
    switch (health.toLowerCase()) {
      case 'healthy':
        return 'success';
      case 'warning':
        return 'warning';
      case 'critical':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'success':
        return 'success';
      case 'running':
      case 'in_progress':
        return 'info';
      case 'failed':
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchData}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!health) return null;

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <BackupIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Backup & Disaster Recovery
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage backups, schedules, and data recovery
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {/* TODO: Open create job dialog */}}
          >
            New Backup Job
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Health Status Card */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <Box textAlign="center">
                <CloudDoneIcon sx={{ fontSize: 60, mb: 1 }} />
                <Typography variant="h5" fontWeight="bold">
                  {health.health_check.overall_health}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Overall Health
                </Typography>
                <Box mt={2}>
                  <LinearProgress
                    variant="determinate"
                    value={health.health_check.health_score}
                    sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.2)' }}
                  />
                  <Typography variant="body2" mt={0.5}>
                    Health Score: {health.health_check.health_score}%
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={9}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {health.health_check.total_jobs}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Backup Jobs</Typography>
                    <Box display="flex" gap={0.5} mt={1} flexWrap="wrap">
                      <Chip
                        label={`${health.health_check.completed_jobs} Completed`}
                        size="small"
                        sx={{ bgcolor: 'rgba(76, 175, 80, 0.3)', color: 'white' }}
                      />
                      {health.health_check.running_jobs > 0 && (
                        <Chip
                          label={`${health.health_check.running_jobs} Running`}
                          size="small"
                          sx={{ bgcolor: 'rgba(33, 150, 243, 0.3)', color: 'white' }}
                        />
                      )}
                      {health.health_check.failed_jobs > 0 && (
                        <Chip
                          label={`${health.health_check.failed_jobs} Failed`}
                          size="small"
                          sx={{ bgcolor: 'rgba(244, 67, 54, 0.3)', color: 'white' }}
                        />
                      )}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {health.health_check.total_backup_size_gb.toFixed(2)} GB
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Storage Used</Typography>
                    <Typography variant="body2" mt={1}>
                      Success Rate: {health.health_check.success_rate}%
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {health.health_check.issues_found}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Issues Found</Typography>
                    {health.health_check.health_issues.length > 0 && (
                      <Typography variant="caption" display="block" mt={1}>
                        {health.health_check.health_issues[0]}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <ScheduleIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {jobs.filter(j => j.schedule_enabled).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Scheduled Jobs
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {jobs.filter(j => j.enabled).length} enabled
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main' }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {history.filter(h => h.status === 'completed').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Successful Backups
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Last 100 runs
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <StorageIcon sx={{ fontSize: 40, color: 'info.main' }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {((health.health_check as any).storage_status?.total_size_gb || 0).toFixed(1)} GB
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Storage Used
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(health.health_check as any).storage_status?.total_backups || 0} backups
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Backup Jobs" icon={<BackupIcon />} iconPosition="start" />
          <Tab label="Backup History" icon={<ScheduleIcon />} iconPosition="start" />
          <Tab label="Storage" icon={<StorageIcon />} iconPosition="start" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>Backup Jobs</Typography>
          <Typography color="text.secondary">Backup jobs table coming soon...</Typography>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>Backup History</Typography>
          <Typography color="text.secondary">Backup history table coming soon...</Typography>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>Storage Usage</Typography>
          <Typography color="text.secondary">Storage details coming soon...</Typography>
        </TabPanel>
      </Card>
    </Box>
  );
};

export default BackupManagement;

